import React from 'react';

// @atlaskit
import { Grid, GridColumn } from '@atlaskit/page';
import Button, { ButtonGroup } from '@atlaskit/button';
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import FieldText from '@atlaskit/field-text';
import Form, { Field, FormFooter, FormSection } from '@atlaskit/form';
import Select from '@atlaskit/select';
import Spinner from '@atlaskit/spinner';
import Tooltip from '@atlaskit/tooltip';

// @source
import { Database as APIs } from '../../../server';
import { fields } from '../../../constants';
import { Messages } from '../../components';
import { Validation } from '../../../tools';
import * as Styled from './styled';
import Layout from '../../templates/Layout';

const initialState = {
  databases: [],
  isLoading: 'configuration',
  message: false,
};

const initialStatic = {
  database: '',
  password: '',
  server: '',
  username: '',
};

class DatabaseSettings extends React.Component {
  constructor(props) {
    super(props);
    this.fields = [];
    this.state = initialState;
    this.static = initialStatic;
    this.unmount = false;
    this.onCloseMessage = this.onCloseMessage.bind(this);
    this.onSubmitCheck = this.onSubmitCheck.bind(this);
    this.onSubmitHandle = this.onSubmitHandle.bind(this);
    this.registerField = this.registerField.bind(this);
  }

  componentWillMount() {
    APIs.configuration({
      success: (data) => {
        this.setStatic(data);
        this.setStateUnmount({ isLoading: '' });
        this.registerFormField();
      },
      error: (messages) => {
        this.setStateUnmount({
          isLoading: '',
          message: (
            <Messages
              items={messages}
              onClose={() => this.setState({ message: false })}
            />
          ),
        });
      },
    });
  }

  componentWillUnmount() {
    this.unmount = true;
  }

  onCloseMessage() {
    this.setState({ message: false });
  }

  onSubmitCheck() {
    const validate = this.form.validate();

    if (!validate.isInvalid) {
      this.setState({
        databases: [],
        isLoading: 'check',
        message: false,
      });

      APIs.check({
        data: {
          password: this.password.state.value,
          server: this.server.state.value,
          username: this.username.state.value,
        },
        success: (databases) => {
          this.setStateUnmount({
            databases,
            isLoading: '',
          });
        },
        error: (messages) => {
          this.setStateUnmount({
            isLoading: '',
            message: (
              <Messages
                items={messages}
                onClose={this.onCloseMessage}
              />
            ),
          });
        },
      });
    }
  }

  onSubmitHandle(event) {
    event.preventDefault();
    const validate = this.form.validate();

    if (!validate.isInvalid) {
      this.setState({
        isLoading: 'save',
        message: false,
      });

      APIs.save({
        data: {
          password: this.password.state.value,
          server: this.server.state.value,
          username: this.username.state.value,
          database: this.static.database,
        },
        success: (messages) => {
          this.setStateUnmount({
            isLoading: '',
            message: (
              <Messages
                appearance="success"
                items={messages}
                onClose={this.onCloseMessage}
              />
            ),
          });
        },
        error: (messages) => {
          this.setStateUnmount({
            isLoading: '',
            message: (
              <Messages
                items={messages}
                onClose={this.onCloseMessage}
              />
            ),
          });
        },
      });
    }
  }

  setStateUnmount(state) {
    if (this.unmount) return;
    this.setState(state);
  }

  setStatic(state) {
    this.static = { ...this.static, ...state };
  }

  registerField(node) {
    this.fields.push(node);
  }

  registerFormField() {
    this.fields.forEach(node => this.form.registerField(node.state.fieldState));
  }

  renderButtonCheck() {
    return (
      <div style={{ position: 'absolute', top: 26, right: 4 }}>
        <Tooltip
          content="Verify server Select"
          delay={0}
          position="right"
        >
          <Button
            appearance="subtle"
            iconBefore={<CheckCircleIcon size="medium" />}
            isLoading={this.state.isLoading === 'check'}
            onClick={this.onSubmitCheck}
          />
        </Tooltip>
      </div>
    );
  }

  renderServerAccount() {
    return (
      <FormSection
        name="server-account"
        title="Server Account"
        description="Fill in the SQL Server information."
      >
        <Field
          {...fields.serverIP.field}
          label="Enter the Server IP address"
          ref={this.registerField}
        >
          <FieldText
            {...fields.serverIP.fieldText}
            disabled={this.state.databases.length}
            ref={(node) => { this.server = node; }}
            value={this.server ? this.server.state.value : this.static.server}
          />
        </Field>
        <Field
          {...fields.username.field}
          label="Enter the Server Username"
          ref={this.registerField}
        >
          <FieldText
            {...fields.username.fieldText}
            disabled={this.state.databases.length}
            ref={(node) => { this.username = node; }}
            value={this.username ? this.username.state.value : this.static.username}
          />
        </Field>
        <Styled.FieldGroupButton>
          <Field
            {...fields.password.field}
            label="Enter the Server Password"
            ref={this.registerField}
          >
            <FieldText
              {...fields.password.fieldText}
              disabled={this.state.databases.length}
              ref={(node) => { this.password = node; }}
              value={this.password ? this.password.state.value : this.static.password}
            />
          </Field>
          {this.state.databases.length ? null : this.renderButtonCheck()}
        </Styled.FieldGroupButton>
      </FormSection>
    );
  }

  renderFormSectionSelect() {
    if (this.state.databases.length) {
      const options = this.state.databases.map(val => ({
        label: val,
        value: val,
      }));

      return (
        <FormSection
          name="select-database"
          title="Select Database"
          description="When using the application, select the database name you want to take action on."
        >
          <Field
            isRequired
            name="database"
            ref={this.registerField}
            validateOnBlur={false}
            validateOnChange
            validators={[Validation.required]}
          >
            <Select
              name="database"
              options={options}
              placeholder="Choose a Database"
              ref={(node) => { this.database = node; }}
              defaultValue={options.find(option => (
                option.value === this.static.database
              ))}
              onChange={(option) => {
                this.setStatic({ database: option.value });
              }}
            />
          </Field>
        </FormSection>
      );
    }

    return <div />;
  }

  renderFormFooter() {
    return (
      <FormFooter>
        <ButtonGroup>
          <Button
            appearance="primary"
            isDisabled={!this.state.databases.length}
            isLoading={this.state.isLoading === 'save'}
            onClick={this.onSubmitHandle}
            type="submit"
          >
            Save settings
          </Button>
        </ButtonGroup>
      </FormFooter>
    );
  }

  render() {
    const { isLoading } = this.state;

    return (
      <Layout isLoading={isLoading === 'configuration'}>
        <Grid>
          <GridColumn medium={3} />
          <GridColumn medium={6}>
            <Form
              name="settings"
              onSubmit={this.onSubmitHandle}
              ref={(form) => { this.form = form; }}
            >
              {this.state.isLoading === 'configuration'
                ? (
                  <Styled.SpinnerScope>
                    <Spinner size="medium" />
                  </Styled.SpinnerScope>
                )
                : (
                  <div>
                    {this.renderServerAccount()}
                    {this.renderFormSectionSelect()}
                    {this.renderFormFooter()}
                  </div>
                )}
            </Form>
            {this.state.message}
          </GridColumn>
        </Grid>
      </Layout>
    );
  }
}

export default DatabaseSettings;
